import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

const TransactionStatementModal = ({ isOpen, onClose, adminToken }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalFee, setTotalFee] = useState(0);

  const getDateRange = () => {
    const today = new Date();
    const startOfToday = new Date(today.setHours(0, 0, 0, 0));
    const endOfToday = new Date(today.setHours(23, 59, 59, 999));
    
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);
    
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);

    switch(dateFilter) {
      case 'today':
        return { startDate: startOfToday, endDate: endOfToday };
      case 'thisWeek':
        return { startDate: startOfWeek, endDate: endOfWeek };
      case 'thisMonth':
        return { startDate: startOfMonth, endDate: endOfMonth };
      case 'custom':
        if (customStartDate && customEndDate) {
          return { 
            startDate: new Date(customStartDate), 
            endDate: new Date(customEndDate)
          };
        }
        return { startDate: null, endDate: null };
      case 'all':
      default:
        return { startDate: null, endDate: null };
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      
      let url = '/api/admin/transactions';
      const params = {};
      
      if (startDate && endDate && dateFilter !== 'all') {
        params.startDate = startDate.toISOString();
        params.endDate = endDate.toISOString();
      }
      
      const response = await axios.get(url, {
        params: params,
        headers: {
          Authorization: `Bearer ${adminToken}`
        }
      });
      
      setTransactions(response.data.transactions);
      setTotalRevenue(response.data.totalRevenue);
      setTotalFee(response.data.totalFee);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTransactions();
    }
  }, [isOpen, dateFilter, customStartDate, customEndDate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDateRangeText = () => {
    switch(dateFilter) {
      case 'today': return 'Today';
      case 'thisWeek': return 'This Week';
      case 'thisMonth': return 'This Month';
      case 'custom': return `${customStartDate} to ${customEndDate}`;
      default: return 'All Transactions';
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>Justerps - Transaction Statement</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            padding: 40px 20px;
            min-height: 100vh;
          }
          
          .receipt-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            overflow: hidden;
            animation: fadeIn 0.5s ease;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          /* Header Section */
          .header {
            background: linear-gradient(135deg, #708238 0%, #8a9e45 100%);
            padding: 40px;
            text-align: center;
            color: white;
          }
          
          .company-logo {
            font-size: 48px;
            font-weight: bold;
            letter-spacing: 2px;
            margin-bottom: 10px;
          }
          
          .company-name {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 5px;
          }
          
          .company-tagline {
            font-size: 14px;
            opacity: 0.9;
            margin-top: 5px;
          }
          
          .statement-title {
            font-size: 24px;
            font-weight: 600;
            margin-top: 20px;
            padding-top: 20px;
            border-top: 2px solid rgba(255,255,255,0.3);
          }
          
          /* Info Cards */
          .info-section {
            padding: 30px 40px;
            background: #f8f9fa;
            border-bottom: 1px solid #e9ecef;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .info-card {
            background: white;
            padding: 20px;
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            border-left: 4px solid #708238;
          }
          
          .info-label {
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #6c757d;
            margin-bottom: 8px;
          }
          
          .info-value {
            font-size: 24px;
            font-weight: bold;
            color: #2c3e50;
          }
          
          .info-sub {
            font-size: 12px;
            color: #6c757d;
            margin-top: 5px;
          }
          
          /* Summary Cards */
          .summary-section {
            padding: 30px 40px;
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          }
          
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 25px;
          }
          
          .summary-card {
            text-align: center;
            padding: 25px;
            border-radius: 16px;
            background: white;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            transition: transform 0.3s ease;
          }
          
          .summary-card:hover {
            transform: translateY(-5px);
          }
          
          .summary-card.primary {
            border-top: 4px solid #708238;
          }
          
          .summary-card.warning {
            border-top: 4px solid #FFA500;
          }
          
          .summary-card.info {
            border-top: 4px solid #17a2b8;
          }
          
          .summary-label {
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #6c757d;
            margin-bottom: 12px;
          }
          
          .summary-amount {
            font-size: 32px;
            font-weight: bold;
            color: #2c3e50;
          }
          
          .summary-currency {
            font-size: 16px;
            color: #6c757d;
          }
          
          /* Table Section */
          .table-section {
            padding: 0 40px 40px 40px;
          }
          
          .table-title {
            font-size: 18px;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #708238;
          }
          
          .transaction-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 14px;
          }
          
          .transaction-table th {
            background: linear-gradient(135deg, #708238 0%, #8a9e45 100%);
            color: white;
            padding: 14px 12px;
            text-align: left;
            font-weight: 600;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .transaction-table td {
            padding: 12px;
            border-bottom: 1px solid #e9ecef;
            color: #495057;
          }
          
          .transaction-table tr:hover {
            background-color: #f8f9fa;
          }
          
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            background: #d4edda;
            color: #155724;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
          }
          
          /* Footer */
          .footer {
            background: #2c3e50;
            color: white;
            padding: 30px 40px;
            text-align: center;
          }
          
          .footer-text {
            font-size: 12px;
            opacity: 0.8;
            line-height: 1.6;
          }
          
          .generated-date {
            margin-top: 15px;
            font-size: 11px;
            opacity: 0.6;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
              margin: 0;
            }
            .receipt-container {
              box-shadow: none;
              border-radius: 0;
            }
            .summary-card:hover {
              transform: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <!-- Header -->
          <div class="header">
            <div class="company-logo">🏢 Justerps</div>
            <div class="company-name">JUSTERPS.COM</div>
            <div class="company-tagline">Enterprise Resource Planning Solutions</div>
            <div class="statement-title">Transaction Statement</div>
          </div>
          
          <!-- Information Section -->
          <div class="info-section">
            <div class="info-grid">
              <div class="info-card">
                <div class="info-label">Statement Period</div>
                <div class="info-value">${getDateRangeText()}</div>
                <div class="info-sub">Generated on ${currentDate}</div>
              </div>
              <div class="info-card">
                <div class="info-label">Statement ID</div>
                <div class="info-value">#JUST-${new Date().getTime()}</div>
                <div class="info-sub">Reference Number</div>
              </div>
              <div class="info-card">
                <div class="info-label">Account Type</div>
                <div class="info-value">Admin Platform</div>
                <div class="info-sub">Justerps Administrator</div>
              </div>
            </div>
          </div>
          
          <!-- Summary Section -->
          <div class="summary-section">
            <div class="summary-grid">
              <div class="summary-card primary">
                <div class="summary-label">Total Transactions</div>
                <div class="summary-amount">${transactions.length}</div>
                <div class="summary-currency">Orders Processed</div>
              </div>
              <div class="summary-card warning">
                <div class="summary-label">Gross Revenue</div>
                <div class="summary-amount">${formatCurrency(totalRevenue)}</div>
                <div class="summary-currency">Total Sales</div>
              </div>
              <div class="summary-card info">
                <div class="summary-label">Platform Commission (20%)</div>
                <div class="summary-amount">${formatCurrency(totalFee)}</div>
                <div class="summary-currency">Justerps Fee</div>
              </div>
            </div>
          </div>
          
          <!-- Transactions Table -->
          <div class="table-section">
            <div class="table-title">📋 Transaction Details</div>
            <table class="transaction-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Date & Time</th>
                  <th>Buyer</th>
                  <th>Seller</th>
                  <th>Amount</th>
                  <th>Platform Fee (20%)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${transactions.map(t => `
                  <tr>
                    <td>${t.orderId.slice(-8)}</td>
                    <td>${formatDate(t.createdAt)}</td>
                    <td><strong>${t.buyerName}</strong></td>
                    <td>${t.sellerName}</td>
                    <td><strong>${formatCurrency(t.amount)}</strong></td>
                    <td>${formatCurrency(t.platformFee)}</td>
                    <td><span class="status-badge">${t.status.toUpperCase()}</span></td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <!-- Footer -->
          <div class="footer">
            <div class="footer-text">
              This is a system-generated statement from Justerps.com<br>
              For any discrepancies, please contact support@justerps.com
            </div>
            <div class="footer-text" style="margin-top: 10px;">
              <strong>Payment Terms:</strong> 20% platform commission deducted from each transaction
            </div>
            <div class="generated-date">
              Generated on ${currentDate} • Valid Statement
            </div>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const exportToCSV = () => {
    const headers = ['Transaction ID', 'Date', 'Buyer', 'Seller', 'Amount (USD)', 'Platform Fee (20%)', 'Status'];
    const csvData = transactions.map(t => [
      t.orderId,
      formatDate(t.createdAt),
      t.buyerName,
      t.sellerName,
      t.amount,
      t.platformFee,
      t.status
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Justerps_Transaction_Statement_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-[#708238] to-[#8a9e45] text-white px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Transaction Statement</h2>
            <p className="text-sm opacity-90">Platform Revenue & Fee Details</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl">×</button>
        </div>

        <div className="border-b p-6 bg-gray-50">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select 
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#708238]"
              >
                <option value="all">All Transactions</option>
                <option value="today">Today</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            
            {dateFilter === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input 
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#708238]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input 
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#708238]"
                  />
                </div>
              </>
            )}
            
            <div className="flex gap-2 ml-auto">
              <button onClick={handlePrint} className="px-4 py-2 bg-[#708238] text-white rounded-lg hover:bg-[#5a6b2c] transition flex items-center gap-2">🖨️ Print</button>
              <button onClick={exportToCSV} className="px-4 py-2 border border-[#708238] text-[#708238] rounded-lg hover:bg-[#708238] hover:text-white transition flex items-center gap-2">📊 Export CSV</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-white border-b">
          <div className="bg-gradient-to-br from-[#708238]/10 to-transparent p-4 rounded-lg border-l-4 border-[#708238]">
            <p className="text-sm text-gray-600">Total Transactions</p>
            <p className="text-2xl font-bold text-[#708238]">{transactions.length}</p>
          </div>
          <div className="bg-gradient-to-br from-[#FFA500]/10 to-transparent p-4 rounded-lg border-l-4 border-[#FFA500]">
            <p className="text-sm text-gray-600">Total Platform Revenue</p>
            <p className="text-2xl font-bold text-[#FFA500]">{formatCurrency(totalRevenue)}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent p-4 rounded-lg border-l-4 border-purple-500">
            <p className="text-sm text-gray-600">Total Platform Fees</p>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(totalFee)}</p>
          </div>
        </div>

        <div id="statement-content" className="overflow-auto max-h-[50vh] p-6">
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No transactions found</p>
              <p className="text-sm text-gray-400 mt-2">Try changing the date range or select "All Transactions"</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Order ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Buyer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Seller</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Fee (20%)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm">{t.orderId.slice(-8)}</td>
                    <td className="px-6 py-4 text-sm">{formatDate(t.createdAt)}</td>
                    <td className="px-6 py-4 text-sm font-medium">{t.buyerName}</td>
                    <td className="px-6 py-4 text-sm">{t.sellerName}</td>
                    <td className="px-6 py-4 text-sm text-[#708238] font-bold">{formatCurrency(t.amount)}</td>
                    <td className="px-6 py-4 text-sm text-[#FFA500]">{formatCurrency(t.platformFee)}</td>
                    <td className="px-6 py-4 text-sm"><span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">{t.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionStatementModal;
